import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../utils/cn";

import { Input } from "./input";
import { textVariants } from "./text";

function InputGroup({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
    return (
        <div
            data-slot="input-group"
            role="group"
            className={cn(
                "group/input-group relative flex h-8 w-full min-w-0 items-center rounded-none border border-line-base bg-surface-sunken",
                "transition-colors",
                // Border-state cascade lives on the group container — the
                // inner Input has `border-0` so the visible border is the
                // group's. Hover/focus/error all drive the container border
                // (per Figma `Solomon-App-v3.0` node 2157:9111). The error
                // state is driven by an inner `[data-slot][aria-invalid=true]`
                // (the Input primitive sets this when `state="error"`); the
                // group reacts via `:has(...)`.
                "hover:border-charcoal-700",
                "has-[:focus-visible]:border-charcoal-500",
                "has-disabled:border-line-neutral has-disabled:text-foreground-imprint",
                "has-[[data-slot][aria-invalid=true]]:border-status-error-500",
                "has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col",
                "has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col",
                "has-[>textarea]:h-auto",
                "has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3",
                "has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5",
                className
            )}
            {...props}
        />
    );
}

const inputGroupAddonVariants = cva(
    cn(
        textVariants({ variant: "body" }),
        "flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-foreground-neutral select-none",
        "group-data-[disabled=true]/input-group:opacity-50",
        "[&>svg:not([class*='size-'])]:size-4"
    ),
    {
        variants: {
            align: {
                "inline-start":
                    "order-first pl-2 has-[>button]:ml-[-0.3rem] has-[>kbd]:ml-[-0.15rem]",
                "inline-end":
                    "order-last pr-2 has-[>button]:mr-[-0.3rem] has-[>kbd]:mr-[-0.15rem]",
                "block-start":
                    "order-first w-full justify-start px-2.5 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2",
                "block-end":
                    "order-last w-full justify-start px-2.5 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2"
            }
        },
        defaultVariants: {
            align: "inline-start"
        }
    }
);

function InputGroupAddon({
    className,
    align = "inline-start",
    ...props
}: React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof inputGroupAddonVariants>): React.ReactElement {
    return (
        <div
            role="group"
            data-slot="input-group-addon"
            data-align={align}
            className={cn(inputGroupAddonVariants({ align }), className)}
            onClick={(e) => {
                if ((e.target as HTMLElement).closest("button")) {
                    return;
                }
                e.currentTarget.parentElement?.querySelector("input")?.focus();
            }}
            {...props}
        />
    );
}

const inputGroupButtonVariants = cva(
    cn(
        textVariants({ variant: "body" }),
        "inline-flex items-center gap-2 shadow-none"
    ),
    {
        variants: {
            size: {
                xs: "h-6 gap-1 rounded-none px-1.5 [&>svg:not([class*='size-'])]:size-3.5",
                sm: "h-7 px-2.5 rounded-none",
                "icon-xs": "size-6 rounded-none p-0 has-[>svg]:p-0",
                "icon-sm": "size-8 rounded-none p-0 has-[>svg]:p-0"
            },
            variant: {
                ghost: "bg-transparent text-foreground-base hover:bg-surface-sunken hover:text-foreground-base",
                default:
                    "bg-surface-inverse-raised text-foreground-inverse hover:bg-surface-inverse-raised/90"
            }
        },
        defaultVariants: {
            size: "xs",
            variant: "ghost"
        }
    }
);

// Supports Base UI's `render` prop pattern via useRender — this is how shadcn's
// Combobox fuses its Trigger with the InputGroupButton (one <button> element
// playing both roles). Without this, passing ComboboxTrigger as children would
// nest two <button>s, which browsers normalize in weird ways (hoisting,
// reordering — the "black box floating outside the input" artifact). Shadcn's
// canonical Button primitive (Batch 4H) will be a drop-in replacement for this.
interface InputGroupButtonProps
    extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "size">,
        VariantProps<typeof inputGroupButtonVariants> {
    render?: React.ReactElement;
}

function InputGroupButton({
    className,
    type = "button",
    variant = "ghost",
    size = "xs",
    render,
    ...props
}: InputGroupButtonProps): React.ReactElement {
    const element = useRender({
        render: render ?? <button type={type} />,
        defaultTagName: "button",
        props: {
            "data-size": size,
            "data-variant": variant,
            className: cn(
                inputGroupButtonVariants({ size, variant }),
                "transition-colors",
                "focus-visible:outline-none",
                "disabled:pointer-events-none disabled:opacity-50",
                className
            ),
            ...props
        }
    });
    return element as React.ReactElement;
}

function InputGroupText({
    className,
    ...props
}: React.HTMLAttributes<HTMLSpanElement>): React.ReactElement {
    return (
        <span
            className={cn(
                textVariants({ variant: "body" }),
                "flex items-center gap-2 text-foreground-neutral",
                "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        />
    );
}

function InputGroupInput({
    className,
    ...props
}: React.ComponentProps<typeof Input>): React.ReactElement {
    return (
        <Input
            data-slot="input-group-control"
            className={cn(
                // Inner Input drops its own border + background — the group
                // owns both. We still keep `aria-invalid:border-0` so a
                // status="error" Input doesn't double-render a border inside
                // the group's already-red one.
                "flex-1 rounded-none border-0 bg-transparent shadow-none",
                "focus-visible:outline-none focus-visible:border-0",
                "hover:border-0 aria-invalid:border-0",
                "disabled:bg-transparent",
                className
            )}
            {...props}
        />
    );
}

export {
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupText,
    InputGroupInput,
    inputGroupAddonVariants,
    inputGroupButtonVariants
};
